<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SearchAPI from 'dashboard/api/search';
import { debounce } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const { uiSettings, updateUISettings } = useUISettings();
const router = useRouter();

const phoneSearch = ref('');
const phoneResults = ref([]);
const phoneSearching = ref(false);
const phoneSearchOpen = ref(false);

const normalizePhone = value =>
  String(value || '').replace(/\D/g, '');

const samePhone = (a, b) => {
  const x = normalizePhone(a);
  const y = normalizePhone(b);
  if (!x || !y) return false;
  if (x === y) return true;
  if (x.length === 10 && y.endsWith(x)) return true;
  if (y.length === 10 && x.endsWith(y)) return true;
  return false;
};

const closePhoneSearch = () => {
  phoneSearchOpen.value = false;
};

const openConversation = conversation => {
  if (!conversation?.id) return;
  phoneSearchOpen.value = false;
  phoneSearch.value = '';
  phoneResults.value = [];

  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: router.currentRoute.value.params.accountId,
      conversation_id: conversation.id,
    },
  });
};

const searchPhone = debounce(async value => {
  const q = normalizePhone(value);

  phoneSearchOpen.value = true;
  phoneResults.value = [];

  if (q.length < 6) return;

  phoneSearching.value = true;

  try {
    const { data } = await SearchAPI.conversations({ q, page: 1 });
    const conversations = data?.payload?.conversations || [];

    phoneResults.value = conversations
      .filter(c => samePhone(c?.meta?.sender?.phone_number, value)
        || samePhone(c?.contact?.phone_number, value)
        || samePhone(c?.meta?.contact?.phone_number, value))
      .slice(0, 10);
  } catch (error) {
    phoneResults.value = [];
  } finally {
    phoneSearching.value = false;
  }
}, 300, false);

const onPhoneSearchInput = value => {
  phoneSearch.value = value;
  searchPhone(value);
};


const route = useRoute();

const dpUiExtensions = ref([]);
const dpUiExtensionsLoaded = ref(false);

const loadDpUiExtensions = async () => {
  if (dpUiExtensionsLoaded.value) return;

  const accountId = route.params.accountId;
  if (!accountId) return;

  try {
    const response = await fetch(
      `/api/v1/accounts/${accountId}/dp_plugins/ui_extensions`,
      {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }
    );

    if (!response.ok) return;

    const payload = await response.json();
    dpUiExtensions.value = payload?.extensions || [];
    dpUiExtensionsLoaded.value = true;
  } catch (_) {
    // Optional plugin UI must never break native Chatwoot.
  }
};

const dpHeaderExtensions = computed(() =>
  dpUiExtensions.value.filter(plugin => {
    const config = plugin?.ui_extensions?.conversation_header;
    return config && config.enabled !== false && config.entry;
  })
);

const openDpPlugin = plugin => {
  router.push({
    path: `/app/accounts/${route.params.accountId}/dp_plugins/${plugin.id}/runtime`,
  });
};

loadDpUiExtensions();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="relative flex items-center justify-between gap-2 px-3 h-[3.25rem]"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center min-w-0 flex-1">
      <h1
        class="text-base font-medium truncate text-n-slate-12 shrink-0"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
      <div class="relative hidden md:block w-[120px] ml-3 shrink-0">
        <input
          :value="phoneSearch"
          type="tel"
          inputmode="numeric"
          autocomplete="off"
          placeholder="Search"
          class="!w-[120px] !h-[20px] !min-w-0 !min-h-0 !px-2 text-xs bg-n-surface-2 !border-2 !border-pink-400 !rounded-full outline-none focus:!border-pink-500 shrink-0"
          @input="onPhoneSearchInput($event.target.value)"
          @focus="phoneSearchOpen = true"
        />

        <div
          v-if="phoneSearchOpen && (phoneResults.length || phoneSearching || phoneSearch.length >= 6)"
          class="absolute z-[100] top-[24px] left-0 w-[360px] rounded-xl border border-n-weak bg-n-surface-1 shadow-xl overflow-hidden"
        >
          <div v-if="phoneSearching" class="px-4 py-3 text-sm text-n-slate-10">
            Searching...
          </div>

          <template v-else-if="phoneResults.length">
            <button
              v-for="item in phoneResults"
              :key="item.id"
              type="button"
              class="w-full px-4 py-3 text-left hover:bg-n-alpha-2 border-b border-n-weak last:border-b-0"
              @click="openConversation(item)"
            >
              <div class="font-medium text-n-slate-12">
                {{ item?.meta?.sender?.name || item?.contact?.name || 'Customer' }}
              </div>
              <div class="text-xs text-n-slate-10 mt-1">
                {{ item?.meta?.sender?.phone_number || item?.contact?.phone_number || '' }}
              </div>
            </button>
          </template>

          <div v-else class="px-4 py-4 text-sm">
            <div class="text-n-slate-10 mb-3">
              No conversation found
            </div>
            <ComposeConversation @close="phoneSearchOpen = false">
              <template #trigger>
                <button
                  type="button"
                  class="w-full h-9 rounded-lg bg-n-brand text-white font-medium"
                >
                  + New Chat
                </button>
              </template>
            </ComposeConversation>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-1">
      <template v-for="plugin in dpHeaderExtensions" :key="`dp-${plugin.id}`">
        <NextButton
          :id="`dp-plugin-${plugin.id}`"
          :icon="plugin.ui_extensions.conversation_header.icon || 'i-lucide-plug'"
          slate
          xs
          faded
          :aria-label="plugin.ui_extensions.conversation_header.label || plugin.name"
          v-tooltip.top="plugin.ui_extensions.conversation_header.label || plugin.name"
          @click="openDpPlugin(plugin)"
        />
      </template>


      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-else class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <SwitchLayout
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle="toggleConversationLayout"
      />
    </div>
  </div>
</template>
